import BaseButton from 'app/components/common/BaseButton';
import { usePageBreakpoint } from 'app/components/layout/Breakpoint';
import { useI18n } from 'app/components/localization';
import ButtonClassNameA, { Size as ButtonSize } from 'app/components/presentation/buttons/ButtonClassNameA';
import Link from 'app/components/presentation/link/Link';
import UnstyledLink from 'app/components/router/Link';
import Logo from 'app/components/svgs/logos/EngooTutorLogo';
import Options from 'app/components/svgs/Options';
import X from 'app/components/svgs/X';
import AuthLink from 'app/components/teacherRecruitment/AuthLink';
import { NumericBreakpoint } from 'app/constants/Breakpoints';
import { GREY_B } from 'app/constants/Colors';
import {
  TeacherRecruitmentSignUpRoute,
  TeacherRecruitmentHelpRoute,
  TeacherRecruitmentRoute,
} from 'app/constants/Routes';
import { buttonReset, listReset } from 'app/constants/Styles';
import { useClasses } from 'app/utils/css';
import React, { FunctionComponent, useState } from 'react';

interface IProps {
  disableTeacherSignupNavigation?: boolean;
}

const Header: FunctionComponent<IProps> = ({ disableTeacherSignupNavigation = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const { t } = useI18n();
  const isWide = usePageBreakpoint() >= NumericBreakpoint.Wide;
  const showMenu = !isWide && isMenuOpen;
  const classes = useClasses({
    container: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'stretch',
      alignItems: 'center',
      padding: '1em 0',
      fontWeight: 600,
      fontSize: isWide ? '12px' : '10px',
    },
    buttonLink: {
      marginLeft: '0.5em',
      zIndex: 1,
      fontSize: isWide ? '14px' : '10px',
    },
    menuButton: {
      ...buttonReset,
      display: isWide ? 'none' : undefined,
    },
    logo: {
      width: 'auto',
      maxWidth: '160px',
      height: isWide ? '25px' : '20px',
    },
    logoContainer: [
      {
        display: 'block',
        // IE11 is adding some margins (presumably due to some spacing issue).
        fontSize: '0',
      },

      !isWide && {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      },
    ],
    space: {
      flexGrow: 1,
    },
    linksContainer: [
      listReset,

      isWide ? {
        display: 'flex',
        alignItems: 'center',
      } : {
        // Positioned relative to the white background wrapping the header.
        position: 'absolute',
        top: '100%',
        left: '0',
        right: '0',
        background: 'white',
        textAlign: 'center',
        overflow: 'hidden',
        transition: 'height 600ms ease',
      },

      {
        height: showMenu ? '100vh' : '0px',
      },
    ],
    linkItem: [
      {
        margin: '0 0.25em',
        fontWeight: 600,
      },

      !isWide && {
        margin: '1.5em 0',
        fontSize: '16px',
      },
    ],
    headerLink: {
      color: GREY_B,
      padding: '0.5em 1em',
      textDecoration: 'none',
    },
  });

  return (
    <div className={classes.container}>
      <BaseButton onClick={toggleMenu} className={classes.menuButton}>
        {isMenuOpen ?
          <X size="40px" color="black" /> :
          <Options size="40px" />}
      </BaseButton>

      <UnstyledLink to={TeacherRecruitmentRoute.getPathname()} className={classes.logoContainer}>
        <Logo className={classes.logo} />
      </UnstyledLink>
      <div className={classes.space} />

      <ul className={classes.linksContainer}>
        <li className={classes.linkItem}>
          <Link to="https://tutor.engoo.com/blog/" className={classes.headerLink}>
            {t('page.teach.header.blog')}
          </Link>
        </li>

        <li className={classes.linkItem}>
          <Link to={TeacherRecruitmentHelpRoute.getPathname()} className={classes.headerLink}>
            {t('page.teach.header.help')}
          </Link>
        </li>

        <li className={classes.linkItem}>
          <AuthLink className={classes.headerLink} />
        </li>
      </ul>

      {!disableTeacherSignupNavigation &&
        <div className={classes.buttonLink}>
          <ButtonClassNameA size={ButtonSize.Small}>{(buttonClassName) => (
            <Link
              to={TeacherRecruitmentSignUpRoute.getPathname()}
              className={buttonClassName}
            >
              {t('page.teach.header.apply-button')}
            </Link>
          )}</ButtonClassNameA>
        </div>}
    </div>
  );
};

export default Header;
